/**
 * This custom hook provides a flexible functionality to sign-in via a social auth provider.
 * The caller only has to provide the provider as argument and this hook will take care about
 * the login via popup as well as the initialization of the roles (if not yet set).
 */

import {useContext, useEffect, useState} from "react";
import {firebaseService} from "../../firebase/Firebase";
import {useIntl} from "react-intl";
import {useSnackbar} from "notistack";
import {FirebaseAuthContext} from "../../firebase/FirebaseAuthProvider";

const useSocialSignIn = () => {
    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    const {clearUserData} = useContext(FirebaseAuthContext)

    /*
     Is only set to true if the authentication actively triggered by the user is in progress.
     This is only the case when the user actively triggers a login action.
     This is necessary in order to prevent an too early redirection in the related UI (SignInSectionMain --> AuthorizationGate).
     It is set to false by default, because otherwise accessing "/" by an already authorized user would not automatically redirect him to the auth page.
     */
    const [manualAuthInProgress, setManualAuthInProgress] = useState(false)

    useEffect(() => {
        firebaseService.auth().currentUser?.getIdToken(true);
    }, [])

    const signInWithSocialProvider = async (authProvider) => {
        authProvider.addScope('email');
        setManualAuthInProgress(true)

        return firebaseService.auth().signInWithPopup(authProvider)
            .then((res) => {
                /*
                This action will trigger the "FirebaseAuthProvider" to call the onAuthStateChanged method.
                But in addition the roles have to be set (see next handler)
                 */
                return res.user.getIdToken()
            })
            .then((idToken) => {
                // Bootstrapping initial roles for the signed-in user
                return fetch('/reg-main/profile/initRole', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({idToken: idToken})
                })
            })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                /*
                  After successful login, let us refresh the id token.
                  IMPORTANT: This will trigger the onIdTokenChanged listener in the FirebaseAuthProvider.
                  IMPORTANT: The refresh is actually only necessary at the first login (as the roles are
                  initially set and are required to be refreshed after). All subsequent roles are immediately
                  retrieved after the login.
                  */
                return firebaseService.auth().currentUser.getIdToken(true);
            })
            .catch((error) => {
                return handleLoginError(error)
            })
            .finally(() => {
                setManualAuthInProgress(false)
            })
    }

    const signInWithEmailAndPassword = (authEmail, authPassword) => {
        setManualAuthInProgress(true)

        firebaseService.auth().signInWithEmailAndPassword(authEmail, authPassword)
            .then((userCredential) => {
                // For the email/password sign-in a refresh is not necessary, as the role has already been set during creation
            })
            .catch((error) => {
                return handleLoginError(error)
            })
            .finally(() => {
                setManualAuthInProgress(false)
            })
    }

    const handleLoginError = (error) => {
        if (error.code === 'auth/user-disabled') {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.signIn.messages.userDisabled.error',
                    defaultMessage: 'Your account has been disabled - please contact andre@salmhofer.net to get more information.'
                }), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 5000
                });
        } else {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.signIn.messages.login.error',
                    defaultMessage: 'Something went wrong while signing-in with your user: {error}'
                }, {error: error.message}), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 5000
                });
        }

        clearUserData()
        return firebaseService.auth().signOut()
    }

    return {signInWithSocialProvider, signInWithEmailAndPassword, manualAuthInProgress}
}
export default useSocialSignIn;